import { Campus } from '@/modules/core/data/types/Campus';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { SerializableStudentSolutionContext } from '@/modules/core/data/types/SerializableStudentSolutionContext';
import { SerializedStudentSolutionContext } from '@/modules/core/data/types/SerializedStudentSolutionContext';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { StudentSolutionsFilterByType } from '@/modules/core/data/types/StudentSolutionsFilterByType';
import * as Sentry from '@sentry/node';
import contextMatcher from '@unly/conditions-matcher';
import { createLogger } from '@/modules/core/logging/logger';
import filter from 'lodash.filter';
import get from 'lodash.get';
import size from 'lodash.size';
import some from 'lodash.some';
import { NextRouter } from 'next/router';
import {
  decodeQueryParameterToJSON,
  getParamFromUrl,
} from '../core/js/url';
import { deserializeStudentSolutionContext } from './studentSolutionContext';

const fileLabel = 'utils/solutionsFilter';
const logger = createLogger({
  fileLabel,
});

export const QUERY_SIMULATION_KEY = 'simulation'; // Key where the serialized simulation data are stored (within the url query)
export const QUERY_SEARCH_FILTER_KEY = 'search'; // Key where the serialized search filter is stored (within the url query)

type ContextMatcherOptions = {
  strictMatch: boolean;
}

/**
 * Filters the given solutions with the given filters.
 *
 * @param {StudentSolution[]} studentSolutions
 * @param {StudentSolutionContext} context
 * @param {object} options
 * @return {StudentSolution[]}
 */
export const filterStudentSolutions = (studentSolutions: StudentSolution[], context: SerializableStudentSolutionContext, options: ContextMatcherOptions = { strictMatch: false }): StudentSolution[] => {
  return filter(studentSolutions, (studentSolution: StudentSolution) => {
    const filter = studentSolution?.eligibilityCriteria;
    const matchResult = contextMatcher(filter, context, options);

    return get(matchResult, 'status', false); // Returns "false" if "status" isn't present
  });
};

/**
 * Initialise the default context to use
 *
 * XXX Potentially temporary implementation, as the workflow should change once we handle loading the context from the chatbot's results
 *
 * @param {string} institutionName
 * @param {Array<Campus> | undefined} campuses
 * @param {Array<EducationalProgram> | undefined} educationalPrograms
 * @param {Array<EducationalProgramStep> | undefined} educationalProgramSteps
 * @param {Array<StudentSolutionsCategory> | undefined} categories
 * @param {string} persona
 * @return {StudentSolutionContext}
 */
export const getDefaultContext = (
  {
    customerRef,
    campuses = undefined,
    educationalPrograms = undefined,
    educationalProgramSteps = undefined,
    categories = undefined,
    persona = undefined,
  }: {
    customerRef: string;
    campuses?: Array<Campus>;
    educationalPrograms?: Array<EducationalProgram>;
    educationalProgramSteps?: Array<EducationalProgramStep>;
    categories?: Array<StudentSolutionsCategory>;
    persona?: string;
  },
): StudentSolutionContext => {
  return {
    customer: {
      ref: customerRef,
    },
    campuses,
    educationalPrograms,
    educationalProgramSteps,
    categories,
    persona,
  };
};

/**
 * Resolves initial context.
 *
 * Use the context stored in the router if available.
 * If not, then use a default context.
 *
 * @param {NextRouter} router
 * @param {string} customerRef
 * @param {Campus[]} campuses
 * @param {EducationalProgram[]} educationalPrograms
 * @param {EducationalProgramStep[]} educationalProgramSteps
 * @param {StudentSolutionsCategory[]} categories
 * @return {StudentSolutionContext}
 */
export const resolveInitialContext = (
  router: NextRouter,
  customerRef: string,
  campuses: Campus[],
  educationalPrograms: EducationalProgram[],
  educationalProgramSteps: EducationalProgramStep[],
  categories: StudentSolutionsCategory[],
): StudentSolutionContext => {
  const defaultSerializedSimulation = '{}';
  // const serializedSimulationFromUrl = get(router, `query.${QUERY_SIMULATION_KEY}`, defaultSerializedSimulation); // XXX See https://github.com/vercel/next.js/issues/10521#issuecomment-716062225
  const serializedSimulationFromUrl: string = getParamFromUrl(QUERY_SIMULATION_KEY, `https://${router?.asPath}`) || defaultSerializedSimulation;
  const defaultContext: StudentSolutionContext = getDefaultContext({
    customerRef: customerRef,
  });

  if (serializedSimulationFromUrl !== defaultSerializedSimulation) {
    // If a simulation was provided in the URL, decode and deserialize it
    const contextFromUrl: StudentSolutionContext = deserializeStudentSolutionContext(decodeQueryParameterToJSON(serializedSimulationFromUrl) as SerializedStudentSolutionContext, campuses, educationalPrograms, educationalProgramSteps, categories);

    // XXX Applying context from url on top of the default one ensures all required fields are always defined
    return {
      ...defaultContext,
      ...contextFromUrl,
    };

  } else {
    // Otherwise, return the default context
    return defaultContext;
  }
};

/**
 * Resolves the initial search filter applied in the url.
 *
 * @param router
 */
export const resolveInitialSearchFilter = (router: NextRouter): string => {
  const encodedSearchFilterFromUrl: string = getParamFromUrl(QUERY_SEARCH_FILTER_KEY, `https://${router?.asPath}`);
  // const encodedSearchFilterFromUrl: string = get(router, `query.${QUERY_SEARCH_FILTER_KEY}`); // XXX See https://github.com/vercel/next.js/issues/10521#issuecomment-716062225
  if (!encodedSearchFilterFromUrl) {
    return '';
  }

  let decodedSearchFilterFromUrl: string;

  try {
    // Decoding might fail (e.g "URI malformed")
    decodedSearchFilterFromUrl = decodeURIComponent(encodedSearchFilterFromUrl);
  } catch (e) {
    Sentry.captureException(e);
    logger.error(e);

    // If decoding failed, default to empty value
    decodedSearchFilterFromUrl = '';
  }

  return decodedSearchFilterFromUrl;
};

/**
 * Filter a set of records if they aren't used as active context filter
 *
 * Used to filter out all records that aren't being used as context filters
 * The goal is to only keep relevant records that are actively being used as filters, to avoid showing too much irrelevant data
 *
 * @param {StudentSolutionContext} context
 * @param {T[]} records
 * @param {StudentSolutionsFilterByType} recordsType
 * @return {T[]}
 */
export const filterRecordsByActiveCriteriaInContextFilter = <T extends Campus | EducationalProgramStep | StudentSolutionsCategory>(context: StudentSolutionContext, records: T[], recordsType: StudentSolutionsFilterByType): T[] => {
  let contextFilterRecords: T[];

  if (recordsType === 'Campus') {
    contextFilterRecords = context?.campuses as T[];
  } else if (recordsType === 'EducationalProgramStep') {
    contextFilterRecords = context?.educationalProgramSteps as T[];
  } else if (recordsType === 'Category') {
    contextFilterRecords = context?.categories as T[];
  } else {
    const message = `"recordsType" value isn't supported, with value: "${recordsType}".`;
    logger.error(message);
    Sentry.captureException(new Error(message));

    contextFilterRecords = undefined; // Won't filter  anything
  }

  if (size(contextFilterRecords) > 0) {
    return filter(records, (record: T) => {
      return some(contextFilterRecords, record);
    });
  } else {
    // If there are no active filter for that criteria, then do not filter
    return records;
  }
};
